/**
 * Move Scoring System
 *
 * Evaluates move quality and assigns scores/grades based on:
 * - Position value (corners, edges, X-squares, etc.)
 * - Number of pieces flipped
 * - Stability bonus
 * - Mobility impact
 */

import {
  BoardPosition,
  PlayerColor,
  Grade,
  GRADE_THRESHOLDS,
  POSITION_VALUES,
} from '@/types/game-types';
import type { MoveScore, ScoreBreakdown } from '@/core/models/Move';
import {
  isCornerMove,
  isEdgeMove,
  isXSquareMove,
  isCSquareMove,
  calculateMobility,
  calculateStability,
} from './move-validator';

/**
 * Evaluate a move and return its score and grade
 */
export const evaluateMove = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
  flippedPositions: BoardPosition[],
): MoveScore => {
  const breakdown = calculateScoreBreakdown(board, position, player, flippedPositions);
  const grade = calculateGrade(breakdown.total);

  return {
    points: breakdown.total,
    grade,
    breakdown: Object.freeze(breakdown),
  };
};

/**
 * Calculate detailed score breakdown for a move
 */
export const calculateScoreBreakdown = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
  flippedPositions: BoardPosition[],
): ScoreBreakdown => {
  const positionValue = calculatePositionValue(position);
  const flippedCount = flippedPositions.length;
  const stabilityBonus = calculateStabilityBonus(board, position, player, flippedPositions);
  const mobilityFactor = calculateMobilityFactor(board, position, player);

  // Base score calculation including mobility difference
  let baseScore = positionValue + flippedCount + stabilityBonus;

  // Add mobility factor (difference between player and opponent mobility)
  const total = Math.max(0, Math.floor(baseScore + mobilityFactor));

  return {
    positionValue,
    flippedCount,
    stabilityBonus,
    mobilityFactor,
    total,
  };
};

/**
 * Calculate position value based on strategic importance
 */
export const calculatePositionValue = (position: BoardPosition): number => {
  if (isCornerMove(position)) {
    return POSITION_VALUES.CORNER; // 25 points - extremely valuable
  }

  if (isXSquareMove(position)) {
    return POSITION_VALUES.X_SQUARE; // -10 points - dangerous, gives opponent corner access
  }

  if (isCSquareMove(position)) {
    return POSITION_VALUES.C_SQUARE; // -6 points - somewhat dangerous
  }

  if (isEdgeMove(position)) {
    return POSITION_VALUES.EDGE; // 6 points - good for stability
  }

  return POSITION_VALUES.NORMAL; // 0 points - neutral position
};

/**
 * Calculate stability bonus for pieces that will become stable
 */
export const calculateStabilityBonus = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
  flippedPositions: BoardPosition[],
): number => {
  let stabilityBonus = 0;

  // Bonus for the placed piece
  const placedStability = calculateStability(board, position, player);
  stabilityBonus += placedStability;

  // Bonus for newly stable flipped pieces
  for (const flippedPos of flippedPositions) {
    const flippedStability = calculateStability(board, flippedPos, player);
    stabilityBonus += Math.floor(flippedStability / 2); // Half value for flipped pieces
  }

  return stabilityBonus;
};

/**
 * Calculate mobility factor as the direct difference between player and opponent mobility
 */
export const calculateMobilityFactor = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
): number => {
  const mobility = calculateMobility(board, position, player);
  const mobilityDifference = mobility.playerMobility - mobility.opponentMobility;

  // Return the direct difference (subtraction result)
  return mobilityDifference;
};

/**
 * Determine grade based on total score
 */
export const calculateGrade = (score: number): Grade => {
  if (score >= GRADE_THRESHOLDS.SSS) {
    return Grade.SSS; // 60+ points
  } else if (score >= GRADE_THRESHOLDS.SS) {
    return Grade.SS; // 45-59 points
  } else if (score >= GRADE_THRESHOLDS.S) {
    return Grade.S; // 30-44 points
  } else if (score >= GRADE_THRESHOLDS.A) {
    return Grade.A; // 18-29 points
  } else if (score >= GRADE_THRESHOLDS.B) {
    return Grade.B; // 10-17 points
  } else {
    return Grade.C; // 0-9 points
  }
};

/**
 * Quick evaluation for AI move sorting (simplified version)
 */
export const quickEvaluateMove = (
  _board: Uint8Array,
  position: BoardPosition,
  _player: PlayerColor,
  flippedPositions: BoardPosition[],
): number => {
  const positionValue = calculatePositionValue(position);
  const flippedCount = flippedPositions.length;

  // Simplified calculation for performance
  return positionValue + flippedCount + (isCornerMove(position) ? 10 : 0);
};

/**
 * Evaluate all valid moves and return sorted by score
 */
export const evaluateAllMoves = (
  board: Uint8Array,
  validMoves: Map<BoardPosition, readonly BoardPosition[]> | Map<BoardPosition, BoardPosition[]>,
  player: PlayerColor,
): Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }> => {
  const evaluatedMoves: Array<{
    position: BoardPosition;
    score: MoveScore;
    flippedPositions: BoardPosition[];
  }> = [];

  for (const [position, flippedPositions] of validMoves.entries()) {
    const score = evaluateMove(board, position, player, [...flippedPositions]);
    evaluatedMoves.push({ position, score, flippedPositions: [...flippedPositions] });
  }

  // Sort by score descending (best moves first)
  return evaluatedMoves.sort((a, b) => b.score.points - a.score.points);
};

/**
 * Get moves above a certain grade threshold
 */
export const getMovesAboveGrade = (
  evaluatedMoves: Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }>,
  minGrade: Grade,
): Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }> => {
  const gradeValues = {
    [Grade.C]: 1,
    [Grade.B]: 2,
    [Grade.A]: 3,
    [Grade.S]: 4,
    [Grade.SS]: 5,
    [Grade.SSS]: 6,
  };
  const minValue = gradeValues[minGrade];

  return evaluatedMoves.filter(move => gradeValues[move.score.grade] >= minValue);
};

/**
 * Get the best move from a list of evaluated moves
 */
export const getBestMove = (
  evaluatedMoves: Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }>,
): { position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] } | null => {
  if (evaluatedMoves.length === 0) {return null;}

  // Find highest scoring move
  let bestMove = evaluatedMoves[0]!;
  for (const move of evaluatedMoves) {
    if (move.score.points > bestMove.score.points) {
      bestMove = move;
    }
  }

  return bestMove;
};

/**
 * Get moves of a specific grade
 */
export const getMovesByGrade = (
  evaluatedMoves: Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }>,
  grade: Grade,
): Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }> => {
  return evaluatedMoves.filter(move => move.score.grade === grade);
};

/**
 * Calculate average score of all moves
 */
export const calculateAverageScore = (
  evaluatedMoves: Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }>,
): number => {
  if (evaluatedMoves.length === 0) {return 0;}

  const totalScore = evaluatedMoves.reduce((sum, move) => sum + move.score.points, 0);
  return totalScore / evaluatedMoves.length;
};

/**
 * Get grade distribution for analytics
 */
export const getGradeDistribution = (
  evaluatedMoves: Array<{ position: BoardPosition; score: MoveScore; flippedPositions: BoardPosition[] }>,
): Record<Grade, number> => {
  const distribution: Record<Grade, number> = {
    [Grade.C]: 0,
    [Grade.B]: 0,
    [Grade.A]: 0,
    [Grade.S]: 0,
    [Grade.SS]: 0,
    [Grade.SSS]: 0,
  };

  for (const move of evaluatedMoves) {
    distribution[move.score.grade]++;
  }

  return distribution;
};

/**
 * Check if a move qualifies for bonus effects (high flip count)
 */
export const shouldTriggerBonusEffects = (flippedCount: number): boolean => {
  return flippedCount >= 16; // Massive flip bonus
};

/**
 * Calculate endgame scoring adjustments
 */
export const calculateEndgameAdjustment = (
  board: Uint8Array,
  position: BoardPosition,
  _player: PlayerColor,
): number => {
  let emptySquares = 0;
  for (let i = 0; i < board.length; i++) {
    if (board[i] === PlayerColor.EMPTY) {
      emptySquares++;
    }
  }

  // In endgame (< 16 empty squares), prioritize flipping count over position
  if (emptySquares < 16) {
    return isCornerMove(position) ? 5 : 0; // Reduced corner bonus
  }

  return 0; // No adjustment in early/mid game
};

/**
 * Format score for display
 */
export const formatScore = (score: MoveScore): string => {
  return `${score.grade}-${score.points}`;
};

/**
 * Format score breakdown for detailed display
 */
export const formatBreakdown = (breakdown: ScoreBreakdown): string => {
  return [
    `Position: ${breakdown.positionValue >= 0 ? '+' : ''}${breakdown.positionValue}`,
    `Flipped: +${breakdown.flippedCount}`,
    `Stability: +${breakdown.stabilityBonus}`,
    `Mobility: ${breakdown.mobilityFactor > 0 ? '+' : ''}${breakdown.mobilityFactor}`,
    `Total: ${breakdown.total}`,
  ].join(' | ');
};